import React, { useState, useEffect, useCallback } from 'react';
import { Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';

const GOLD = '#B8892A';
const GOLD_LIGHT = '#D4A843';
const BD = '#DDD7CE';
const BD2 = '#EAE6DF';
const BD3 = '#F2EFE9';
const BG = '#FAF8F5';
const BG2 = '#F4F1EC';
const BG3 = '#EDE9E2';
const INK = '#1A1410';
const INK2 = '#352C22';
const INK3 = '#6B5E4E';
const INK4 = '#A8998A';
const INK5 = '#D0C5B8';
const GRN = '#2A6645';
const RED = '#9B2335';
const BLUE = '#1A4F8C';
const AMBER = '#8C5A0A';
// const JPY_CNY = 0.049;

const STATUS_CFG = {
  pending_review: { labelKey: 'mandates.statusPending', color: AMBER, bg: 'rgba(140,90,10,.08)', border: 'rgba(140,90,10,.2)', progress: 15, dot: AMBER },
  approved: { labelKey: 'mandates.statusApproved', color: BLUE, bg: 'rgba(26,79,140,.08)', border: 'rgba(26,79,140,.2)', progress: 30, dot: BLUE },
  executing: { labelKey: 'mandates.statusBidding', color: BLUE, bg: 'rgba(26,79,140,.08)', border: 'rgba(26,79,140,.2)', progress: 50, dot: BLUE },
  queued:    { labelKey: 'mandates.statusBidding', color: BLUE, bg: 'rgba(26,79,140,.08)', border: 'rgba(26,79,140,.2)', progress: 50, dot: BLUE },
  won: { labelKey: 'mandates.statusWon', color: GRN, bg: 'rgba(42,102,69,.08)', border: 'rgba(42,102,69,.2)', progress: 75, dot: GRN },
  lost: { labelKey: 'mandates.statusLost', color: INK4, bg: 'rgba(168,153,138,.08)', border: 'rgba(168,153,138,.2)', progress: 100, dot: INK5 },
  // expired = 拍卖已结束但未成交（自动过期，保证金已退回）；客户视角等同「未成交」
  expired: { labelKey: 'mandates.statusExpired', color: INK4, bg: 'rgba(168,153,138,.08)', border: 'rgba(168,153,138,.2)', progress: 100, dot: INK5 },
  // bid_failed = 系统自动出价多次均失败（未中标，保证金已退回）
  bid_failed: { labelKey: 'mandates.statusBidFailed', color: RED, bg: 'rgba(155,35,53,.06)', border: 'rgba(155,35,53,.2)', progress: 100, dot: RED },
  rejected: { labelKey: 'mandates.statusRejected', color: RED, bg: 'rgba(155,35,53,.08)', border: 'rgba(155,35,53,.2)', progress: 100, dot: RED },
  cancelled: { labelKey: 'mandates.statusCancelled', color: RED, bg: 'rgba(155,35,53,.08)', border: 'rgba(155,35,53,.2)', progress: 0, dot: RED },
};

// ── 保证金资金状态（严格对应后端解冻逻辑，杜绝显示歧义）──
// 冻结中(占用): pending_review/approved/queued/executing
// 已退回:        lost/expired/rejected/cancelled/bid_failed（后端均已解冻）
// 转订单结算:    won（不退，转入订单）
const DEPOSIT_RETURNED_STATUSES = ['lost', 'expired', 'rejected', 'cancelled', 'bid_failed'];
function depositState(status) {
  if (status === 'won') return 'order';
  if (DEPOSIT_RETURNED_STATUSES.includes(status)) return 'returned';
  return 'frozen';
}

const STATUS_TABS = [
  { key: '', labelKey: 'common.all' },
  { key: 'pending_review', labelKey: 'mandates.statusPending' },
  { key: 'active', labelKey: 'mandates.statusBidding' },
  { key: 'won', labelKey: 'mandates.statusWon' },
  { key: 'done', labelKey: 'mandates.statusDone' },
];

// 「竞拍中」的口径：已通过审核(approved)/已排队(queued)/执行中(executing) 均属竞拍中
// 统一常量，避免统计卡、筛选 tab、点击筛选三处口径漂移(曾漏 queued 导致上下数字不一致)
const ACTIVE_STATUSES = ['approved', 'queued', 'executing'];

const SUMMARY_ITEMS = [
  { key: 'all', labelKey: 'common.all', color: GOLD, filter: () => true },
  { key: 'pending', labelKey: 'mandates.statusPending', color: AMBER, filter: m => m.status === 'pending_review' },
  { key: 'active', labelKey: 'mandates.statusBidding', color: BLUE, filter: m => ACTIVE_STATUSES.includes(m.status) },
  { key: 'won', labelKey: 'mandates.statusWon', color: GRN, filter: m => m.status === 'won' },
  { key: 'lost', labelKey: 'mandates.statusLost', color: RED, filter: m => m.status === 'lost' },
];

const CAT_EMOJI = { bag: '👜', watch: '⌚', jewelry: '💍', art: '🏺', toy: '🧸', other: '📦' };

function formatJPY(val) {
  if (val == null) return '--';
  return '¥' + Number(val).toLocaleString('ja-JP');
}

function formatCNY(val) {
  if (val == null) return '--';
  return '¥' + Number(val).toFixed(2);
}

function useCountdown(endAt) {
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    if (!endAt) { setText(''); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('ended'); setUrgent(false); return; }
      setUrgent(diff < 3600);
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      if (d > 0) setText(`${d}d ${h}h`);
      else setText(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  return { text, urgent };
}

function getImage(mandate) {
  try {
    const urls = typeof mandate.lot_image_urls === 'string' ? JSON.parse(mandate.lot_image_urls) : mandate.lot_image_urls;
    return Array.isArray(urls) ? urls[0] : null;
  } catch { return null; }
}

/* -- Session Countdown Bar -- */
function SessionCountdownBar({ session }) {
  const { t } = useTranslation();
  const { text, urgent } = useCountdown(session.end_at || session.sessionEnd);
  if (!text) return null;
  const displayText = text === 'ended' ? t('browse.ended') : text;
  const urgencyClass = urgent ? 'urgent' : text === 'ended' ? '' : 'ok';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
      background: 'rgba(26,22,18,.03)', borderBottom: `1px solid ${BD3}`, flexWrap: 'wrap',
    }}>
      <span style={{ fontSize: 11, color: INK3 }}>{t('mandates.sessionCountdown')}</span>
      <span style={{ fontSize: 12, fontWeight: 500, color: INK2 }}>{session.house_name || session.house}</span>
      <span style={{ fontSize: 11, color: INK3 }}>{session.session_name || session.name}</span>
      <span style={{
        fontFamily: 'monospace', fontSize: 15, fontWeight: 600,
        color: urgencyClass === 'urgent' ? RED : urgencyClass === 'ok' ? GRN : INK3,
      }}>
        {displayText}
      </span>
    </div>
  );
}

/* -- Mandate Card -- */
function MandateCard({ mandate, onCancel, cancellingId, isMobile, goodsPay, onGoodsCheckout, goodsCheckoutId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const stRaw = STATUS_CFG[mandate.status] || { labelKey: mandate.status, color: INK4, bg: 'rgba(168,153,138,.08)', border: 'rgba(168,153,138,.2)', progress: 0, dot: INK4 };
  const st = { ...stRaw, label: t(stRaw.labelKey) };
  const img = getImage(mandate);
  const emoji = CAT_EMOJI[mandate.category] || '📦';
  // 只有"审核中"才允许取消；审核通过后即锁定（自动审核场景下客户根本看不到取消按钮）
  const canCancel = mandate.status === 'pending_review';

  const timelineSteps = [
    { label: t('mandates.timelineMandateSubmitted'), done: true, time: mandate.created_at, desc: `${t('mandates.maxAuthorizedBid')} ${formatJPY(mandate.max_bid_jpy)}` },
    { label: t('mandates.timelineReview'), done: ['approved', 'queued', 'executing', 'won', 'lost', 'expired', 'bid_failed', 'cancelled', 'shipped', 'delivered'].includes(mandate.status), failed: mandate.status === 'rejected', current: mandate.status === 'pending_review', desc: mandate.status === 'pending_review' ? t('mandates.reviewUsually2Hours') : mandate.status === 'rejected' ? mandate.reject_reason : undefined },
    { label: t('mandates.statusBidding'), done: ['won', 'lost', 'expired', 'bid_failed', 'shipped', 'delivered'].includes(mandate.status), current: ['executing', 'approved', 'queued'].includes(mandate.status), pending: !['approved', 'queued', 'executing', 'won', 'lost', 'expired', 'bid_failed', 'shipped', 'delivered'].includes(mandate.status) },
    { label: t('mandates.timelineBidResult'), done: ['won', 'shipped', 'delivered'].includes(mandate.status), failed: ['lost', 'expired', 'bid_failed'].includes(mandate.status), pending: !['won', 'lost', 'expired', 'bid_failed', 'shipped', 'delivered'].includes(mandate.status) },
    { label: t('mandates.timelineComplete'), done: ['delivered'].includes(mandate.status), pending: mandate.status !== 'delivered' },
  ];

  return (
    <div style={{
      background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4,
      overflow: 'hidden', transition: 'all 0.2s',
      boxShadow: expanded ? '0 4px 20px rgba(26,20,16,0.08)' : 'none',
    }}>
      {/* Progress bar */}
      <div style={{ height: 3, background: BD3 }}>
        <div style={{ height: '100%', width: st.progress + '%', background: st.dot, transition: 'width 0.8s ease' }} />
      </div>

      {/* Card head */}
      <div onClick={() => setExpanded(e => !e)} style={{ padding: isMobile ? '12px 12px' : '14px 18px', display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, cursor: 'pointer' }}>
        <div style={{ width: 48, height: 48, flexShrink: 0, borderRadius: 3, background: BG2, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 24 }}>{emoji}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <span style={{ fontFamily: 'monospace', fontSize: 10, color: INK4, letterSpacing: 1 }}>{mandate.mandate_no || mandate.id}</span>
            {mandate.category && (
              <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '1px 6px', borderRadius: 2, background: BG3, color: INK4, border: `1px solid ${BD2}`, letterSpacing: 1 }}>
                {mandate.category.toUpperCase()}
              </span>
            )}
            {mandate.session_name && (
              <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '1px 6px', borderRadius: 2, background: 'rgba(43,92,230,.08)', color: BLUE }}>
                {mandate.session_name}
              </span>
            )}
          </div>
          <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 14, fontWeight: 500, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 4 }}>
            {mandate.lot_title || '--'}
          </div>
          <div style={{ display: 'flex', gap: 12, fontFamily: 'monospace', fontSize: 10, color: INK4 }}>
            {mandate.house_name && <span>{mandate.house_name}</span>}
            <span>· {mandate.created_at ? dayjs(mandate.created_at).format('MM-DD HH:mm') : '--'}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {mandate.status === 'won' && mandate.won_price_jpy
            ? <div style={{ fontFamily: 'monospace', fontSize: 16, color: GRN, fontWeight: 500 }}>{formatJPY(mandate.won_price_jpy)}</div>
            : <div style={{ fontFamily: 'monospace', fontSize: 16, color: GOLD, fontWeight: 500 }}>{formatJPY(mandate.max_bid_jpy)}</div>
          }
          {mandate.status === 'won'
            ? <div style={{ fontFamily: 'monospace', fontSize: 9, color: GRN, marginTop: 2 }}>✓ {t('mandates.won')}</div>
            : mandate.deposit_freeze_cny != null && (() => {
                // 保证金去向随状态明确标注：已退回 / 冻结中 / 转订单结算（资金相关，杜绝歧义）
                const ds = depositState(mandate.status);
                const tag = ds === 'returned' ? t('mandates.depReturned') : ds === 'order' ? t('mandates.depToOrder') : t('mandates.depFrozen');
                const col = ds === 'returned' ? GRN : ds === 'order' ? BLUE : INK4;
                return <div style={{ fontFamily: 'monospace', fontSize: 10, color: col, marginTop: 2 }}>{tag} {formatCNY(mandate.deposit_freeze_cny)}</div>;
              })()
          }
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontFamily: 'monospace', fontSize: 9, padding: '3px 8px', borderRadius: 2,
            letterSpacing: 1, border: `1px solid ${st.border}`, background: st.bg, color: st.color, whiteSpace: 'nowrap',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: st.dot, display: 'inline-block', flexShrink: 0, ...(['executing', 'queued'].includes(mandate.status) ? { animation: 'bdot 1s infinite' } : {}) }} />
            {st.label}
          </div>
        </div>
        <div style={{ fontSize: 11, color: INK5, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'none', flexShrink: 0, marginLeft: 4 }}>▾</div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${BD2}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) 280px' }}>
            {/* Timeline */}
            <div style={{ padding: isMobile ? '16px 14px' : '20px 22px', borderRight: isMobile ? 'none' : `1px solid ${BD2}`, borderBottom: isMobile ? `1px solid ${BD2}` : 'none' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: 2, color: INK4, textTransform: 'uppercase', marginBottom: 16 }}>{t('mandates.mandateProgress')}</div>
              <div style={{ position: 'relative', paddingLeft: 20 }}>
                <div style={{ position: 'absolute', left: 5, top: 8, bottom: 8, width: 1, background: BD2 }} />
                {timelineSteps.map((tl, i) => {
                  const dotColor = tl.failed ? RED : tl.done ? GRN : tl.current ? GOLD : BD;
                  return (
                    <div key={i} style={{ position: 'relative', marginBottom: i < timelineSteps.length - 1 ? 16 : 0 }}>
                      <div style={{
                        position: 'absolute', left: -20, top: 3, width: 11, height: 11,
                        borderRadius: '50%', border: `2px solid ${dotColor}`,
                        background: (tl.done || tl.failed) ? dotColor : tl.current ? GOLD_LIGHT : BG,
                        zIndex: 1,
                        ...(tl.current ? { boxShadow: `0 0 0 3px rgba(184,137,42,.15)` } : {}),
                      }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                        <span style={{
                          fontSize: 13, fontWeight: tl.current ? 500 : 400,
                          color: tl.pending && !tl.current ? INK4 : tl.failed ? RED : INK2,
                        }}>{tl.label}</span>
                        {tl.time && <span style={{ fontFamily: 'monospace', fontSize: 10, color: INK4 }}>{dayjs(tl.time).format('MM-DD HH:mm')}</span>}
                      </div>
                      {tl.desc && <div style={{ fontSize: 12, color: INK4, marginTop: 3, lineHeight: 1.6 }}>{tl.desc}</div>}
                    </div>
                  );
                })}
              </div>

              {['executing', 'queued'].includes(mandate.status) && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(26,79,140,.08)', border: '1px solid rgba(26,79,140,.2)', borderRadius: 3, fontSize: 12, color: BLUE, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: BLUE, animation: 'bdot 1s infinite' }} />
                  {t('mandates.autoBidEngineRunning')}
                </div>
              )}
              {mandate.status === 'won' && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(42,102,69,.08)', border: '1px solid rgba(42,102,69,.2)', borderRadius: 3, fontSize: 12, color: GRN }}>
                  {t('mandates.wonCongrats')}
                </div>
              )}
              {/* 入札結果メッセージ（成功 or 失敗） */}
              {mandate.bid_message && mandate.status === 'queued' && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(42,102,69,.08)', border: '1px solid rgba(42,102,69,.2)', borderRadius: 3, fontSize: 12, color: GRN }}>
                  ✅ {mandate.bid_message}
                </div>
              )}
              {mandate.bid_message && mandate.status === 'approved' && mandate.executed_at && (
                <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(155,35,53,.06)', border: '1px solid rgba(155,35,53,.2)', borderRadius: 3, fontSize: 12, color: RED }}>
                  ⚠️ {t('mandates.autoBidWarning', { message: mandate.bid_message })}
                </div>
              )}
            </div>

            {/* Side panel */}
            <div style={{ padding: 20, background: BG }}>
              {/* Result banner for won/lost */}
              {mandate.status === 'won' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(42,102,69,.08)', border: `1px solid rgba(42,102,69,.25)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: GRN, marginBottom: 4 }}>🎉 {t('mandates.bidSuccess')}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: GRN }}>{formatJPY(mandate.won_price_jpy)}</div>
                  {mandate.won_price_jpy && <div style={{ fontSize: 10, color: INK4, marginTop: 2 }}>≈ {formatCNY(mandate.won_price_jpy * mandate.exchange_rate)} CNY</div>}
                </div>
              )}
              {mandate.status === 'lost' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(155,35,53,.06)', border: `1px solid rgba(155,35,53,.2)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: RED, marginBottom: 4 }}>{t('mandates.notWon')}</div>
                  <div style={{ fontSize: 12, color: INK3 }}>
                    {mandate.lot_final_price
                      ? t('mandates.hammerPriceExceeded', { price: formatJPY(mandate.lot_final_price) })
                      : t('mandates.wonByOtherBuyer')}
                  </div>
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>{t('mandates.depositUnfrozenContinue')}</div>
                </div>
              )}
              {mandate.status === 'expired' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(168,153,138,.1)', border: `1px solid rgba(168,153,138,.3)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: INK3, marginBottom: 4 }}>{t('mandates.endedNotWon')}</div>
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>{t('mandates.depositUnfrozenContinue')}</div>
                </div>
              )}
              {mandate.status === 'bid_failed' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(155,35,53,.06)', border: `1px solid rgba(155,35,53,.2)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: RED, marginBottom: 4 }}>{t('mandates.statusBidFailed')}</div>
                  <div style={{ fontSize: 12, color: INK3 }}>{mandate.bid_message || t('mandates.bidFailedNote')}</div>
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>{t('mandates.depositUnfrozenContinue')}</div>
                </div>
              )}
              {mandate.status === 'rejected' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(155,35,53,.08)', border: `1px solid rgba(155,35,53,.2)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: RED, marginBottom: 4 }}>{t('mandates.statusRejected')}</div>
                  {mandate.reject_reason && <div style={{ fontSize: 12, color: INK3 }}>{mandate.reject_reason}</div>}
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>{t('mandates.depositUnfrozenContinue')}</div>
                </div>
              )}
              {mandate.status === 'cancelled' && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(168,153,138,.1)', border: `1px solid rgba(168,153,138,.3)`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: INK3, marginBottom: 4 }}>{t('mandates.statusCancelled')}</div>
                  <div style={{ fontSize: 10, color: INK4, marginTop: 4 }}>{t('mandates.depositUnfrozenContinue')}</div>
                </div>
              )}

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: 2, color: INK4, textTransform: 'uppercase', marginBottom: 10 }}>{t('mandates.priceDetails')}</div>
                {[
                  { k: t('mandates.maxAuthorizedBid'), v: formatJPY(mandate.max_bid_jpy), vc: GOLD },
                  mandate.status === 'won' && mandate.won_price_jpy
                    ? { k: t('mandates.actualDealPrice'), v: formatJPY(mandate.won_price_jpy), vc: GRN }
                    : null,
                  // 保证金行：金额 + 当前资金状态（冻结中/已退回/转订单结算），避免「已退回却写冻结」的歧义
                  mandate.deposit_freeze_cny != null
                    ? (() => {
                        const ds = depositState(mandate.status);
                        const tag = ds === 'returned' ? t('mandates.depReturned') : ds === 'order' ? t('mandates.depToOrder') : t('mandates.depFrozen');
                        const vc = ds === 'returned' ? GRN : ds === 'order' ? BLUE : INK2;
                        return { k: t('mandates.frozenDeposit'), v: `${formatCNY(mandate.deposit_freeze_cny)} CNY · ${tag}`, vc };
                      })()
                    : { k: t('mandates.frozenDeposit'), v: '--' },
                  mandate.lot_estimate_low ? { k: t('lotDetail.estimateRange'), v: `${formatJPY(mandate.lot_estimate_low)} ~ ${formatJPY(mandate.lot_estimate_high)}` } : null,
                ].filter(Boolean).map(row => (
                  <div key={row.k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', borderBottom: `1px solid ${BD3}`, fontFamily: 'monospace', fontSize: 11 }}>
                    <span style={{ color: INK4 }}>{row.k}</span>
                    <span style={{ color: row.vc || INK2 }}>{row.v}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                {/* 货款支付（合规拆分：货款走 olshop 商城） */}
                {mandate.status === 'won' && (
                  goodsPay?.status === 'paid' ? (
                    <div style={{
                      width: '100%', padding: '8px 16px', background: 'rgba(42,102,69,.08)',
                      border: '1px solid rgba(42,102,69,.25)', borderRadius: 3, textAlign: 'center',
                      fontSize: 12, color: GRN,
                    }}>✓ {t('mandates.goodsPaid')}</div>
                  ) : (
                    <button onClick={() => onGoodsCheckout(mandate)} disabled={goodsCheckoutId === mandate.id} style={{
                      width: '100%', padding: '9px 16px', background: GOLD, border: `1px solid ${GOLD}`, borderRadius: 3,
                      cursor: goodsCheckoutId === mandate.id ? 'default' : 'pointer', fontSize: 12, color: '#fff',
                      fontWeight: 600, opacity: goodsCheckoutId === mandate.id ? 0.6 : 1, transition: 'all 0.15s',
                    }}>
                      {goodsCheckoutId === mandate.id
                        ? t('mandates.goodsPayLoading')
                        : `${t('mandates.goodsPay')} ${formatJPY(mandate.won_price_jpy)}`}
                    </button>
                  )
                )}
                {mandate.lot_id && (
                  <button onClick={() => navigate(`/lots/${mandate.lot_id}`)} style={{
                    width: '100%', padding: '8px 16px', background: 'transparent', border: `1px solid ${BD}`, borderRadius: 3,
                    cursor: 'pointer', fontSize: 12, color: INK3, transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = INK3; }}
                  >{t('mandates.viewLotDetail')}</button>
                )}
                {canCancel && (
                  <button onClick={() => onCancel(mandate)} disabled={cancellingId === mandate.id} style={{
                    width: '100%', padding: '8px 16px', background: 'transparent', border: '1px solid rgba(155,35,53,.3)', borderRadius: 3,
                    cursor: 'pointer', fontSize: 12, color: RED, transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(155,35,53,.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >{cancellingId === mandate.id ? t('mandates.cancelling') : t('mandates.withdrawMandate')}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes bdot{0%,100%{opacity:1}50%{opacity:.2}}`}</style>
    </div>
  );
}

/* -- Main Mandates Page -- */
export default function Mandates() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => new URLSearchParams(window.location.search).get('status') || '');
  const [cancellingId, setCancellingId] = useState(null);
  const [confirmingMandate, setConfirmingMandate] = useState(null);
  // 货款支付（合规拆分：货款走 olshop）——按 mandate_id 归并的最新一条记录
  const [goodsPayMap, setGoodsPayMap] = useState({});
  const [goodsCheckoutId, setGoodsCheckoutId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Deposit sidebar state
  const [depositBalance, setDepositBalance] = useState(null);
  const [recentLedger, setRecentLedger] = useState([]);

  const fetchMandates = useCallback(async () => {
    setLoading(true);
    try {
      // limit=200 确保拿到全部委托（后端默认20条）
      const { data } = await api.get('/mandates', { params: { limit: 200 } });
      setMandates(data.data || []);
    } catch (err) {
      message.error(t('mandates.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchMandates(); }, [fetchMandates]);

  // Fetch deposit balance and recent ledger for sidebar
  useEffect(() => {
    const fetchDeposit = async () => {
      try {
        const [balRes, ledgerRes] = await Promise.all([
          api.get('/deposits/balance'),
          api.get('/deposits/ledger', { params: { limit: 5 } }),
        ]);
        setDepositBalance(balRes.data.data || balRes.data || {});
        setRecentLedger(ledgerRes.data.data || ledgerRes.data || []);
      } catch (err) {
        console.error('Failed to fetch deposit info', err);
      }
    };
    fetchDeposit();
  }, []);

  // 拉取当前客户的货款支付记录（一次拉全，按 mandate 归并）
  const fetchGoodsPayments = useCallback(async () => {
    try {
      const { data } = await api.get('/goods-payment/mine');
      setGoodsPayMap(data.byMandate || {});
    } catch (err) {
      // 表未建/服务未配等情况静默降级（按钮仍可点，点击时会给明确报错）
      console.error('Failed to fetch goods payments', err);
    }
  }, []);
  useEffect(() => { fetchGoodsPayments(); }, [fetchGoodsPayments]);

  // 点击「去支付货款」→ 推单 olshop → 跳转付款页
  const handleGoodsCheckout = async (mandate) => {
    setGoodsCheckoutId(mandate.id);
    try {
      const { data } = await api.post(`/goods-payment/${mandate.id}/checkout`);
      if (data.payUrl) {
        window.location.href = data.payUrl; // 跳转 olshop 付款页（含 SSO 免登录令牌）
      } else {
        message.error(t('mandates.goodsPayFailed'));
      }
    } catch (err) {
      const d = err.response?.data;
      if (d?.status === 'paid') {
        message.info(t('mandates.goodsPaid'));
        fetchGoodsPayments();
      } else {
        message.error(d?.error || t('mandates.goodsPayFailed'));
      }
    } finally {
      setGoodsCheckoutId(null);
    }
  };

  const handleCancel = (mandate) => setConfirmingMandate(mandate);

  const doCancel = async () => {
    if (!confirmingMandate) return;
    const mandate = confirmingMandate;
    setConfirmingMandate(null);
    setCancellingId(mandate.id);
    try {
      await api.put(`/mandates/${mandate.id}/cancel`);
      message.success(t('mandates.cancelSuccess'));
      fetchMandates();
    } catch (err) {
      message.error(err.response?.data?.message || t('mandates.cancelFailed'));
    } finally {
      setCancellingId(null);
    }
  };

  // Filter mandates by status
  const getFiltered = () => {
    if (!statusFilter) return mandates;
    if (statusFilter === 'active') return mandates.filter(m => ACTIVE_STATUSES.includes(m.status));
    if (statusFilter === 'done') return mandates.filter(m => ['shipping', 'delivered'].includes(m.status));
    return mandates.filter(m => m.status === statusFilter);
  };
  const filteredMandates = getFiltered();

  // Group by session
  const groupBySession = (list) => {
    const groups = {};
    const noSession = [];
    list.forEach(m => {
      const key = m.session_id || m.session_name || '';
      if (!key) { noSession.push(m); return; }
      if (!groups[key]) {
        groups[key] = {
          session_id: m.session_id,
          session_name: m.session_name || key,
          house_name: m.house_name,
          end_at: m.session_end_at,
          type: m.session_type,
          mandates: [],
        };
      }
      groups[key].mandates.push(m);
    });
    const result = Object.values(groups);
    if (noSession.length > 0) {
      result.push({ session_name: t('mandates.otherMandates'), mandates: noSession });
    }
    return result;
  };

  const sessionGroups = groupBySession(filteredMandates);
  const getCount = (filterFn) => mandates.filter(filterFn).length;

  // Check if there's an active live session
  const hasLiveSession = mandates.some(m => m.status === 'executing');

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)', padding: '0 0 60px' }}>
      {/* Live auction bar */}
      {hasLiveSession && (
        <div style={{
          background: INK, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
          maxWidth: 1200, margin: '0 auto',
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#5FC98A', animation: 'lpulse 1s infinite', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', flex: 1 }}>
            <strong style={{ color: '#fff' }}>{t('mandates.hasActiveMandates')}</strong> {t('mandates.statusBidding')}
          </span>
          <button onClick={() => navigate('/auction')} style={{
            padding: '6px 14px', background: GOLD, color: '#fff', border: 'none', borderRadius: 3,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>● {t('mandates.enterAuctionLive')}</button>
        </div>
      )}

      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: `1px solid ${BD2}`, padding: isMobile ? '16px' : '24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 24, fontWeight: 600, color: INK, letterSpacing: 2 }}>{t('mandates.title')}</div>
            <div style={{ fontSize: 13, color: INK4, marginTop: 4 }}>
              {t('common.total')} <strong>{mandates.length}</strong> {t('mandates.mandateCount')} · {t('mandates.groupedBySession')} · {t('mandates.clickToExpand')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigate('/auction-hub')} style={{
              padding: '8px 16px', borderRadius: 3, border: `1px solid ${BD}`, background: 'transparent',
              fontSize: 12, cursor: 'pointer', color: INK3,
            }}>+ {t('mandates.newMandate')}</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '16px 16px 0' : '24px 32px 0', display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) 280px', gap: 24, alignItems: 'start' }}>
        {/* Main content */}
        <div>
        {/* Summary strip */}
        <div style={isMobile ? {
          display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 6, marginBottom: 20,
        } : { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <style>{`div::-webkit-scrollbar{display:none}`}</style>
          {SUMMARY_ITEMS.map(item => {
            const count = getCount(item.filter);
            const isActive = (item.key === 'all' && statusFilter === '') ||
              statusFilter === item.key ||
              (item.key === 'active' && statusFilter === 'active');
            return (
              <div key={item.key} onClick={() => {
                if (item.key === 'all') setStatusFilter('');
                else if (item.key === 'active') setStatusFilter('active');
                else setStatusFilter(item.key === 'pending' ? 'pending_review' : item.key);
              }} style={{
                background: isActive ? BG2 : '#fff', border: `1px solid ${BD2}`, borderRadius: 3,
                padding: isMobile ? '9px 4px' : '14px 16px', cursor: 'pointer', transition: 'all 0.15s',
                position: 'relative', overflow: 'hidden',
                boxShadow: isActive ? '0 2px 12px rgba(26,20,16,0.08)' : 'none',
              }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.boxShadow = '0 2px 12px rgba(26,20,16,0.06)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: item.color }} />
                <div style={{ fontFamily: 'monospace', fontSize: 22, color: item.color, lineHeight: 1, marginBottom: 3 }}>{count}</div>
                <div style={{ fontSize: 11, color: INK4 }}>{t(item.labelKey)}</div>
              </div>
            );
          })}
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${BD2}`, marginBottom: 20, overflowX: isMobile ? 'auto' : 'visible', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          {STATUS_TABS.map(tab => {
            const isActive = statusFilter === tab.key;
            const count = tab.key === '' ? mandates.length :
              tab.key === 'active' ? mandates.filter(m => ACTIVE_STATUSES.includes(m.status)).length :
              tab.key === 'done' ? mandates.filter(m => ['shipping', 'delivered'].includes(m.status)).length :
              mandates.filter(m => m.status === tab.key).length;
            return (
              <div key={tab.key} onClick={() => setStatusFilter(tab.key)} style={{
                padding: '10px 18px', fontSize: 13, cursor: 'pointer',
                color: isActive ? GOLD : INK4,
                borderBottom: `2px solid ${isActive ? GOLD : 'transparent'}`,
                transition: 'all 0.14s', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              }}>
                {t(tab.labelKey)}
                <span style={{
                  fontFamily: 'monospace', fontSize: 9, padding: '1px 5px', borderRadius: 10,
                  background: isActive ? 'rgba(184,137,42,.12)' : BG3, color: isActive ? GOLD : INK4,
                }}>{count}</span>
              </div>
            );
          })}
        </div>

        {/* Mandate list grouped by session */}
        <Spin spinning={loading}>
          {filteredMandates.length === 0 && !loading ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4 }}>
              <div style={{ fontSize: 44, opacity: 0.3, marginBottom: 12 }}>📋</div>
              <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 18, color: INK3, marginBottom: 6 }}>{t('mandates.empty')}</div>
              <div style={{ fontSize: 13, color: INK4, marginBottom: 20 }}>{t('mandates.emptyDesc')}</div>
              <button onClick={() => navigate('/auction-hub')} style={{
                padding: '8px 20px', background: GOLD, border: 'none', borderRadius: 3,
                cursor: 'pointer', color: '#fff', fontSize: 13,
              }}>{t('mandates.browseLots')}</button>
            </div>
          ) : (
            sessionGroups.length > 0 ? (
              sessionGroups.map((group, gi) => (
                <div key={gi} style={{ marginBottom: 28 }}>
                  {/* Session countdown bar */}
                  {group.end_at && (
                    <SessionCountdownBar session={group} />
                  )}
                  {/* Session group header */}
                  {group.session_name && group.session_name !== t('mandates.otherMandates') && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                      background: BG2, border: `1px solid ${BD2}`, borderRadius: '4px 4px 0 0',
                      marginBottom: 0,
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: INK2 }}>{group.house_name}</span>
                      <span style={{ fontSize: 12, color: INK3, flex: 1 }}>{group.session_name}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: INK4 }}>
                        {group.mandates.length} {t('mandates.mandateCount')}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {group.mandates.map(mandate => (
                      <MandateCard
                        key={mandate.id}
                        mandate={mandate}
                        onCancel={handleCancel}
                        cancellingId={cancellingId}
                        isMobile={isMobile}
                        goodsPay={goodsPayMap[mandate.id]}
                        onGoodsCheckout={handleGoodsCheckout}
                        goodsCheckoutId={goodsCheckoutId}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {filteredMandates.map(mandate => (
                  <MandateCard key={mandate.id} mandate={mandate} onCancel={handleCancel} cancellingId={cancellingId} isMobile={isMobile} goodsPay={goodsPayMap[mandate.id]} onGoodsCheckout={handleGoodsCheckout} goodsCheckoutId={goodsCheckoutId} />
                ))}
              </div>
            )
          )}
        </Spin>
        </div>{/* end main content */}

        {/* Right sidebar — hidden on mobile */}
        {!isMobile && <div style={{ position: 'sticky', top: 80 }}>
          {/* Deposit Card */}
          <div style={{
            background: INK, borderRadius: 4, padding: 16,
            marginBottom: 24, color: '#fff', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{
              position: 'absolute', width: 200, height: 200, borderRadius: '50%',
              background: 'rgba(184,137,42,.08)', top: -60, right: -60,
            }} />
            <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: 2, color: 'rgba(255,255,255,.4)', marginBottom: 6, position: 'relative' }}>
              {t('deposit.title')}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 28, color: GOLD_LIGHT, fontWeight: 500, lineHeight: 1, marginBottom: 12, position: 'relative' }}>
              {depositBalance ? formatCNY(Number(depositBalance.available_cny || 0) + Number(depositBalance.frozen_cny || 0)) : '--'}
            </div>
            <div style={{ display: 'flex', gap: 20, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 14, color: 'rgba(255,255,255,.7)' }}>
                  {depositBalance ? formatCNY(depositBalance.frozen_cny) : '--'}
                </span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginTop: 2 }}>{t('deposit.frozen')}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 14, color: 'rgba(255,255,255,.7)' }}>
                  {depositBalance ? formatCNY(depositBalance.available_cny) : '--'}
                </span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,.3)', marginTop: 2 }}>{t('deposit.available')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, position: 'relative' }}>
              <button onClick={() => navigate('/deposit')} style={{
                flex: 1, padding: 7, borderRadius: 3, border: 'none',
                background: GOLD, color: '#fff',
                fontFamily: 'monospace', fontSize: 9, letterSpacing: 1, cursor: 'pointer',
                textAlign: 'center', transition: 'all .15s',
              }}>{t('deposit.recharge')}</button>
              <button onClick={() => navigate('/deposit/detail')} style={{
                flex: 1, padding: 7, borderRadius: 3,
                border: '1px solid rgba(255,255,255,.2)', background: 'transparent',
                color: 'rgba(255,255,255,.5)',
                fontFamily: 'monospace', fontSize: 9, letterSpacing: 1, cursor: 'pointer',
                textAlign: 'center', transition: 'all .15s',
              }}>{t('deposit.detail')}</button>
            </div>
          </div>

          {/* Recent Ledger */}
          <div style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 4, padding: 16 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: 2, color: INK4, marginBottom: 12 }}>{t('mandates.recentLedger')}</div>
            {recentLedger.length === 0 ? (
              <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 12, color: INK4 }}>{t('mandates.noLedgerRecords')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recentLedger.map((item, i) => {
                  const isCredit = item.type === 'deposit' || item.type === 'unfreeze';
                  const isFreeze = item.type === 'freeze';
                  const amountColor = isCredit ? GRN : isFreeze ? AMBER : RED;
                  const prefix = isCredit ? '+' : isFreeze ? '冻 ' : '';
                  return (
                    <div key={item.id || i} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', background: '#fff', border: `1px solid ${BD2}`,
                      borderRadius: 3, fontSize: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: INK2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.label || item.description || '--'}
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: 9, color: INK4, marginTop: 2 }}>
                          {item.meta || (item.created_at ? dayjs(item.created_at).format('MM-DD HH:mm') : '')}
                        </div>
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500, flexShrink: 0, color: amountColor }}>
                        {prefix}{formatCNY(Math.abs(item.amount))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <span
                onClick={() => navigate('/deposit/detail')}
                style={{ fontFamily: 'monospace', fontSize: 9, color: INK4, cursor: 'pointer', letterSpacing: 1 }}
              >{t('mandates.viewAllBills')} →</span>
            </div>
          </div>
        </div>}{/* end sidebar */}
      </div>

      {/* Confirm cancel modal */}
      {confirmingMandate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 6, padding: 32, maxWidth: 400, width: '90%' }}>
            <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 18, fontWeight: 600, color: INK, marginBottom: 12 }}>{t('mandates.confirmCancelTitle')}</div>
            <div style={{ fontSize: 13, color: INK3, lineHeight: 1.7, marginBottom: 24 }}>
              {t('mandates.confirmCancelDesc', { title: confirmingMandate.lot_title || confirmingMandate.mandate_no })}<br />
              {t('mandates.confirmCancelDepositReturn')}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmingMandate(null)} style={{
                flex: 1, padding: 10, background: 'transparent', border: `1px solid ${BD}`, borderRadius: 3,
                cursor: 'pointer', fontSize: 13, color: INK3,
              }}>{t('mandates.notNow')}</button>
              <button onClick={doCancel} style={{
                flex: 1, padding: 10, background: RED, border: 'none', borderRadius: 3,
                cursor: 'pointer', fontSize: 13, color: '#fff',
              }}>{t('common.confirm')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes lpulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(1.2)}}`}</style>
    </div>
  );
}
